import { QUESTIONNAIRE_CODE } from 'lib/constants';

export enum AppErrorCode {
  MEASUREMENT_CODE_FACE_UNDETECTED_ERROR = 80001,
}

export enum InfoType {
  NONE,
  INSTRUCTION,
  SUCCESS,
}

export interface InfoData {
  type: InfoType;
  message?: string;
  instruction?: string;
}

export enum VideoReadyState {
  HAVE_ENOUGH_DATA = 4,
}

export interface NavItem {
  name: string;
  icon: string;
  href?: string;
  size?: number;
  onClick?: () => void;
}

export interface ReadingData {
  name: string;
  value: number | string;
  unit?: string;
  color: string;
  intro: string;
  colorRange: {
    range: string[] | number[];
    color: string;
  }[];
  colorRangeWithCategory: {
    range: string[] | number[];
    color: string;
    eng_category?: string;
  }[];
  category: string;
  valueRange: number[] | string[];
  short_intro: string;
  long_intro: string;
  key: string;
}

export interface ReadingDataWithSubParameters extends ReadingData {
  display: string;
  subParameters: ReadingData[];
  categories?: Record<string, ReadingData>;
}

export type CategorizedReadingData = Record<
  string,
  ReadingDataWithSubParameters[]
>;

export interface UserAttributes {
  user_id: string;
  birthdate: string;
  gender: string;
  given_name: string;
  family_name: string;
  middle_name: string;
  locale: null | string;
  height: string;
  weight: string;
  height_unit: string;
  weight_unit: string;
  created_at: string;
  lastmodified_at: string;
  email?: string;
  phone_number?: string;
  profile_id?: string;
}

export interface Questionnaire {
  created_at: string;
  lastmodified_at: string;
  q_id: string;
  eng_question: string;
  spanish_question: string;
  question_type: string;
  eng_choices: string;
  spanish_choices: string;
  multi_select: boolean;
  question_sequence: number;
  answer_id?: string;
  selected_eng_option?: string | string[];
  selected_spanish_option?: string | string[];
}

export interface ReportQuestionnaire {
  q_id: string;
  eng_question: string;
  spanish_question: string;
  eng_choices: string;
  spanish_choices: string;
  question_type: string;
  multi_select: boolean;
  eng_options: string;
  spanish_options: string;
}
export interface ReportQuestion {
  q_id: string;
  question_type: QuestionType;
  eng_question: string;
  spanish_question: string;
  eng_choices: string;
  spanish_choices: string;
  validation: null;
  linked_image: null;
  path_type: string;
  completion_flag: boolean;
  answer_id?: string;
  token_id?: string;
  retry?: number;
  retryDelay?: number;
}

export enum USER_ACTIVITY {
  SCAN_INTENT_FAILED = 'scan_intent_failed',
  START_SCAN = 'start_scan',
  SCAN_ERROR = 'scan_error',
  END_SCAN = 'end_scan',
  STOP_SCAN = 'stop_scan',
  UPDATE_ADMIN_DETAIL = 'update_admin_detail',
  ADDING_PROFILE = 'adding_profile',
  UPDATE_PROFILE_DETAIL = 'update_profile_detail',
  DELETE_ACCOUNT = 'delete_account',
  DELETE_PROFILE = 'delete_profile',
  DELETE_REPORT = 'delete_report',
  CREATE_QUESTIONNAIRE = 'create_questionnaire',
  UPDATE_QUESTIONNAIRE = 'update_questionnaire',
  RESTART_QUESTIONNAIRE = 'restart_questionnaire',
  SUBMIT_QUESTIONNAIRE = 'submit_questionnaire',
  SCAN_AI_REPORT = 'scan_ai_report',
  ONBOARDING_STEP = 'onboarding_step',
  SHARE_REPORT = 'share_report',
  LOGIN = 'login',
  LOGOUT = 'logout',
  CHANGE_PASSWORD = 'change_pwd',
  GENERATE_QR = 'generate-qr',
  DELETE_VOICE_SCAN_REPORT = 'delete_voice_scan_report',
  VOICE_SCAN_REPORT_SHARE = 'voice_scan_report_share',
  VOICE_SCAN_UPLOAD_START = 'voice_scan_upload_start',
  VOICE_SCAN_UPLOAD_COMPLETE = 'voice_scan_upload_complete',
  VOICE_SCAN_UPLOAD_ERROR = 'voice_scan_upload_error',
  VOICE_SCAN_RECORDING_STOP = 'voice_scan_recording_stop',
  VOICE_SCAN_REPORT_GENERATION_COMPLETE = 'voice_scan_report_generation_complete',
  APP_NOTIFICATIONS = 'app_notifications',
}

export enum QuestionType {
  TEXT = 'text',
  MULTI_SELECT = 'multi-select',
  SINGLE_SELECT = 'single-select',
  DROPDOWN = 'dropdown',
  SUBMITTED = 'submitted',
  FILE_UPLOAD = 'file-upload',
  FINAL_MESSAGE = 'final_message',
}

export type SortOrder = 'asc' | 'desc';

export type SuccessResponse = { success: boolean };

export interface ReportInsight {
  attention: Record<string, string>;
  prevention: Record<string, string>;
  test_and_next_step: string[] | Record<string, string>;
  intervention: Record<string, string>;
}

export enum WSScanStatus {
  START_SCAN = 'start_scan',
  STOP_SCAN = 'stop_scan',
  END_SCAN = 'end_scan',
  SCAN_ERROR = 'scan_error',
  SESSION_EXPIRED = 'session_expired',
}
export interface WSScanStatusData {
  connection_id: string;
  status: WSScanStatus;
  error: Record<'header' | 'description', string>[];
}

export interface SessionError {
  error_title: string;
  error_msg: string;
  timestamp: string;
  session_id: string;
  socket_flag: boolean;
}

export type QuestionnaireItem = {
  q_id: string;
  eng_question: string;
  spanish_question: string;
  question_type: QuestionType;
  eng_choices: string | null;
  spanish_choices: string | null;
  linked_image: null;
  user_eng_choices: string;
  user_spanish_choices: string;
  answer_id?: string;
  path_type: string;
  token_id?: string;
  retry?: number;
  retryDelay?: number;
  answer?: string;
  code: QUESTIONNAIRE_CODE;
  question_sequence: number;
  final_question: boolean;
  choice?: any;
};
